import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { posApi, saasApi, authApi } from '@/services/api';
import { showBrandModel } from '@/constants/industryFeatures';
import { createItemRouted, createStockRouted, isLocalFirstMode, listLocalItems } from '@/offline';
import { useSyncStore } from '@/store/syncStore';
import { resolveFileUrl } from '@/utils/fileUrl';

const resizeImage = (file: File, maxWidth = 800, maxHeight = 800): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Canvas toBlob failed'));
            }
          },
          'image/jpeg',
          0.85
        );
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};
import { useTranslation } from 'react-i18next';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import Sidebar from '@/components/common/Sidebar';
import { getApiErrorMessage } from '@/utils/supplierValidation';
import TrialBanner from '@/components/common/TrialBanner';
import { Package, Plus, Search, Pencil, X, Barcode, Printer, Menu, Upload, Trash2, AlertCircle } from 'lucide-react';
import { isCompanyAdmin, isManager, type POSItem, type ItemVariant, type PackageLimits, type PosSettings, type POSCustomField } from '@/types';
import SupplierSelect from '@/components/common/SupplierSelect';
import ImeiSerialListEditor from '@/components/common/ImeiSerialListEditor';
import PrintBarcodeModal from '@/components/items/PrintBarcodeModal';
import StockIdentityPreview from '@/components/items/StockIdentityPreview';
import Pagination, { LIST_PAGE_SIZE } from '@/components/common/Pagination';
import EntityImportWizard from '@/components/dataImport/EntityImportWizard';
import ItemsImportChooser from '@/components/dataImport/ItemsImportChooser';
import type { EntityImportConfig } from '@/components/dataImport/types';
import BulkPrintLabelsModal from '@/components/items/BulkPrintLabelsModal';
import BulkPriceUpdateModal from '@/components/items/BulkPriceUpdateModal';
import CatalogExportModal from '@/components/items/CatalogExportModal';
import BulkProductUpdateModal from '@/components/items/BulkProductUpdateModal';
import ItemsPageToolbar from '@/components/items/ItemsPageToolbar';
import CustomFieldsModal from '@/components/items/CustomFieldsModal';
import StockReportTab from '@/components/items/StockReportTab';
import { formatMoney, getCurrencySymbol } from '@/utils/currency';
import { getDefaultVatRate } from '@/data/locales';

export default function ItemsPage() {
  const { t } = useTranslation();
  const { toggleSidebar } = useUIStore();
  const { role } = useAuthStore();
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<'All' | 'Product' | 'Service'>('All');
  const [filterCategory, setFilterCategory] = useState('');
  const [page, setPage] = useState(0);
  const [activeTab, setActiveTab] = useState<'Products List' | 'Stock Report'>('Products List');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<POSItem | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    id: string;
    label: string;
    type: 'customer' | 'supplier' | 'product' | 'variant';
    errorMessage?: string;
  } | null>(null);
  const [showImportChooser, setShowImportChooser] = useState(false);
  const [showBulkProductUpdateModal, setShowBulkProductUpdateModal] = useState(false);
  const [importConfig, setImportConfig] = useState<EntityImportConfig | null>(null);
  const [showBulkPrintModal, setShowBulkPrintModal] = useState(false);
  const [showBulkPriceModal, setShowBulkPriceModal] = useState(false);
  const [showCatalogExportModal, setShowCatalogExportModal] = useState(false);
  const [showCustomFieldsModal, setShowCustomFieldsModal] = useState(false);
  const canManagePrices = isCompanyAdmin(role) || isManager(role);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryFormData, setCategoryFormData] = useState({ category_name: '', description: '', category_type: 'Product' });
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [brandFormData, setBrandFormData] = useState({ brand_name: '', description: '' });
  const [brandError, setBrandError] = useState('');
  const [showModelModal, setShowModelModal] = useState(false);
  const [modelFormData, setModelFormData] = useState({ model_name: '', description: '' });
  const [modelError, setModelError] = useState('');

  // Stock Adjustments State
  const [stockModalType, setStockModalType] = useState<'in' | 'out' | null>(null);
  const [selectedStockItem, setSelectedStockItem] = useState<POSItem | null>(null);
  const [stockFormData, setStockFormData] = useState({
    supplier: '',
    purchase_date: new Date().toISOString().split('T')[0],
    unit_price: '',
    quantity: '',
    stock_out_type: 'Damage/Loss',
    variant: '',
    batch_no: '',
    expiry_date: '',
    serials: [] as { imei: string; mac_address: string }[],
  });

  const [formData, setFormData] = useState({
    item_name: '', item_code: '', item_type: 'Product' as 'Product' | 'Service',
    category: '', brand: '', custom_model: '', sale_price: '', cost_price: '', unit_of_measurement: '',
    barcode: '', has_variants: false, allow_fractional_qty: true, maintain_stock: true,
    is_weighted: false, plu_code: '', track_batches: false, track_serials: false,
    image: '', wholesale_price: '', reseller_price: '', minimum_price: '', opening_balance: '',
    tax_rate: '',
  });
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});
  const [variantForm, setVariantForm] = useState({
    variant_name: '', price: '', cost_price: '', unit_of_measurement: '', barcode: '',
    wholesale_price: '', reseller_price: '', minimum_price: '', opening_balance: '',
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const resizedBlob = await resizeImage(file);
      const useLocalImage =
        isLocalFirstMode() ||
        !navigator.onLine ||
        !useSyncStore.getState().isServerReachable;

      const toDataUrl = () =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result || ''));
          reader.onerror = () => reject(new Error('Failed to read image'));
          reader.readAsDataURL(resizedBlob);
        });

      if (useLocalImage) {
        // Store in IndexedDB via CatalogItem.image for immediate POS/Items display.
        const dataUrl = await toDataUrl();
        if (!dataUrl.startsWith('data:')) throw new Error('Invalid image data');
        setFormData((prev) => ({ ...prev, image: dataUrl }));
        return;
      }

      try {
        const uploadFile = new File(
          [resizedBlob],
          file.name.substring(0, file.name.lastIndexOf('.')) + '.jpg',
          { type: 'image/jpeg' },
        );
        const uploadFormData = new FormData();
        uploadFormData.append('file', uploadFile);
        uploadFormData.append('is_private', '0');
        const res = await saasApi.uploadFile(uploadFormData);
        const fileUrl = res.data.message.file_url;
        setFormData((prev) => ({ ...prev, image: fileUrl }));
      } catch (uploadErr) {
        // Local First / flaky network: keep local data URL so save still works.
        console.warn('Image upload failed; storing locally', uploadErr);
        const dataUrl = await toDataUrl();
        setFormData((prev) => ({ ...prev, image: dataUrl }));
      }
    } catch (err) {
      console.error('Failed to upload image', err);
      alert('Failed to process image. Please try again.');
    } finally {
      setUploadingImage(false);
      // Allow re-selecting the same file
      e.target.value = '';
    }
  };
  const [editingVariant, setEditingVariant] = useState<ItemVariant | null>(null);
  const [showVariantForm, setShowVariantForm] = useState(false);
  const [variantError, setVariantError] = useState('');
  const [formError, setFormError] = useState('');
  const [stockInNotice, setStockInNotice] = useState('');
  const [printTarget, setPrintTarget] = useState<POSItem | null>(null);
  const [printVariants, setPrintVariants] = useState<ItemVariant[]>([]);
  const qc = useQueryClient();

  useEffect(() => {
    setPage(0);
  }, [search, filterType, filterCategory]);

  const { data: posSettings } = useQuery({
    queryKey: ['posSettings'],
    queryFn: async () => {
      const res = await posApi.getPosSettings();
      return res.data.message as PosSettings;
    },
  });

  const { data: companyProfile } = useQuery({
    queryKey: ['companyProfile'],
    queryFn: async () => {
      const res = await authApi.getCompanyProfile();
      return res.data.message as { business_type?: string };
    },
  });
  const brandModelVisible = showBrandModel(companyProfile?.business_type);

  const { data: customFields = [] } = useQuery({
    queryKey: ['customFields', 'active'],
    queryFn: async () => {
      const res = await posApi.getCustomFields();
      return res.data.message as POSCustomField[];
    },
  });

  const { data: itemsData, isLoading } = useQuery({
    queryKey: ['items', search, filterType, filterCategory, page, posSettings?.sell_only_products, useSyncStore.getState().posSyncMode],
    queryFn: async () => {
      if (isLocalFirstMode()) {
        let items = await listLocalItems({
          search: search || undefined,
          item_type: posSettings?.sell_only_products
            ? 'Product'
            : filterType === 'All'
              ? undefined
              : (filterType as 'Product' | 'Service'),
        });
        if (filterCategory) items = items.filter((i) => i.category === filterCategory);
        const start = page * LIST_PAGE_SIZE;
        return {
          items: items.slice(start, start + LIST_PAGE_SIZE) as POSItem[],
          total: items.length,
        };
      }
      const res = await posApi.getItems({
        search,
        item_type: posSettings?.sell_only_products ? 'Product' : (filterType === 'All' ? undefined : filterType),
        category: filterCategory || undefined,
        start: page * LIST_PAGE_SIZE,
        limit: LIST_PAGE_SIZE,
      });
      const message = res.data.message;
      return {
        items: message.items as POSItem[],
        total: (message.total as number) || 0,
      };
    },
  });

  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => { const res = await posApi.getCategories(); return res.data.message; },
  });

  // Fetch UOM list
  const { data: uomData } = useQuery({
    queryKey: ['uoms'],
    queryFn: async () => { const res = await posApi.getUOMs();      return res.data.message;
    }
  });

  const { data: brandsData } = useQuery({
    queryKey: ['posBrands'],
    queryFn: async () => {
      const res = await posApi.getBrands();
      return res.data.message;
    }
  });

  const { data: modelsData } = useQuery({
    queryKey: ['posModels'],
    queryFn: async () => {
      const res = await posApi.getModels();
      return res.data.message;
    }
  });

  const { data: packageLimits } = useQuery({
    queryKey: ['packageLimits'],
    queryFn: async () => {
      const res = await posApi.getPackageLimits();
      return res.data.message as PackageLimits;
    },
  });

  const productLimits = packageLimits?.products;

  const { data: editStockBalance } = useQuery({
    queryKey: ['itemStock', editingItem?.name],
    queryFn: async () => {
      const res = await posApi.getItemStock(editingItem!.name);
      return res.data.message.balance as number;
    },
    enabled: showModal && !!editingItem && editingItem.item_type === 'Product',
  });

  const { data: stockModalVariants = [] } = useQuery({
    queryKey: ['itemVariants', selectedStockItem?.name, 'stockModal'],
    queryFn: async () => {
      const res = await posApi.getItemVariants(selectedStockItem!.name);
      return res.data.message as ItemVariant[];
    },
    enabled: !!stockModalType && !!selectedStockItem?.has_variants,
  });

  const selectedVariantStock = stockFormData.variant
    ? (stockModalVariants.find((v) => v.name === stockFormData.variant)?.stock_quantity ?? 0)
    : null;

  const { data: itemVariants = [], refetch: refetchVariants } = useQuery({
    queryKey: ['itemVariants', editingItem?.name],
    queryFn: async () => {
      const res = await posApi.getItemVariants(editingItem!.name);
      return res.data.message as ItemVariant[];
    },
    enabled: showModal && !!editingItem?.name && !!formData.has_variants,
  });

  const saveMutation = useMutation({
    mutationFn: async (data: unknown) => {
      const payload = editingItem
        ? { ...(data as Record<string, unknown>), name: editingItem.name, _action: 'update' }
        : (data as Record<string, unknown>);
      const saved = await createItemRouted(payload);
      return { data: { message: saved } };
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['items'] });
      qc.invalidateQueries({ queryKey: ['posItems'] });
      const saved = res.data?.message as POSItem | undefined;
      if (!editingItem && formData.has_variants && saved?.name) {
        setEditingItem(saved);
      } else {
        setShowModal(false);
        resetForm();
      }
    },
    onError: (err: unknown) => {
      const message = (err as { response?: { data?: { message?: string } }; message?: string })
        ?.response?.data?.message || (err as Error)?.message || 'Failed to save item';
      setFormError(message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (name: string) => posApi.deleteItem(name),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['items'] });
      qc.invalidateQueries({ queryKey: ['posItems'] });
      setDeleteConfirm(null);
    },
    onError: (err: any) => {
      const errMsg = getApiErrorMessage(err, 'Failed to delete product');
      setDeleteConfirm((prev) => prev ? { ...prev, errorMessage: errMsg } : null);
    },
  });

  const handleDeleteItem = async (id: string, label: string, hasStock: boolean) => {
    if (hasStock) {
      setDeleteConfirm({
        id,
        label,
        type: 'product',
        errorMessage: `This product cannot be deleted because it has active stock quantity in hand.`
      });
      return;
    }
    try {
      await posApi.deleteItem(id, 1);
      setDeleteConfirm({ id, label, type: 'product' });
    } catch (err: any) {
      const errMsg = getApiErrorMessage(err, 'Cannot delete product due to linked transaction history.');
      setDeleteConfirm({
        id,
        label,
        type: 'product',
        errorMessage: errMsg
      });
    }
  };

  const categoryMutation = useMutation({
    mutationFn: (data: unknown) => posApi.createCategory(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['categories'] });
      setShowCategoryModal(false);
      setCategoryFormData({ category_name: '', description: '', category_type: 'Product' });
    },
  });

  const brandMutation = useMutation({
    mutationFn: (data: unknown) => posApi.createBrand(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['posBrands'] });
      qc.invalidateQueries({ queryKey: ['brandsList'] });
      setShowBrandModal(false);
      setBrandFormData({ brand_name: '', description: '' });
      setBrandError('');
    },
    onError: (err: unknown) => {
      const message = (err as { response?: { data?: { message?: string } }; message?: string })
        ?.response?.data?.message || (err as Error)?.message || 'Failed to create brand';
      setBrandError(message);
    },
  });

  const modelMutation = useMutation({
    mutationFn: (data: unknown) => posApi.createModel(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['posModels'] });
      qc.invalidateQueries({ queryKey: ['modelsList'] });
      setShowModelModal(false);
      setModelFormData({ model_name: '', description: '' });
      setModelError('');
    },
    onError: (err: unknown) => {
      const message = (err as { response?: { data?: { message?: string } }; message?: string })
        ?.response?.data?.message || (err as Error)?.message || 'Failed to create model';
      setModelError(message);
    },
  });

  // API Integration Mutation for Stock In
  const stockInMutation = useMutation({
    mutationFn: async (innerData: Record<string, unknown>) => {
      const result = await createStockRouted('in', innerData);
      return { data: { message: typeof result === 'string' ? { name: result } : result } };
    },
    onSuccess: (res) => {
      const balance = res.data?.message?.new_balance;
      if (balance !== undefined) {
        setStockInNotice(`Stock in saved. New balance: ${balance}`);
        setTimeout(() => setStockInNotice(''), 5000);
      } else if (isLocalFirstMode()) {
        setStockInNotice('Stock in saved locally (pending sync)');
        setTimeout(() => setStockInNotice(''), 5000);
      }
      qc.invalidateQueries({ queryKey: ['items'] });
      qc.invalidateQueries({ queryKey: ['itemStock'] });
      qc.invalidateQueries({ queryKey: ['itemVariants'] });
      setStockModalType(null);
      setSelectedStockItem(null);
    },
  });

  // API Integration Mutation for Stock Out
  const stockOutMutation = useMutation({
    mutationFn: async (innerData: Record<string, unknown>) => {
      return createStockRouted('out', innerData);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['items'] });
      qc.invalidateQueries({ queryKey: ['packageLimits'] });
      qc.invalidateQueries({ queryKey: ['itemStock'] });
      qc.invalidateQueries({ queryKey: ['itemVariants'] });
      setStockModalType(null);
      setSelectedStockItem(null);
    },
  });
  const variantSaveMutation = useMutation({
    mutationFn: async () => {
      if (!editingItem) throw new Error('Save the item first');
      const price = parseFloat(variantForm.price);
      const wholesale_price = parseFloat(variantForm.wholesale_price) || 0;
      const reseller_price = parseFloat(variantForm.reseller_price) || 0;
      const minimum_price = parseFloat(variantForm.minimum_price) || 0;
      if (!variantForm.variant_name.trim()) throw new Error('Variant name is required');
      if (isNaN(price) || price < 0) throw new Error('Price must be zero or greater');
      if (wholesale_price < 0 || reseller_price < 0 || minimum_price < 0) throw new Error('Prices cannot be negative');
      const payload = {
        variant_name: variantForm.variant_name.trim(),
        price,
        wholesale_price,
        reseller_price,
        minimum_price,
        cost_price: parseFloat(variantForm.cost_price) || 0,
        unit_of_measurement: formData.unit_of_measurement || 'pcs',
        barcode: variantForm.barcode.trim() || undefined,
        opening_balance: !editingVariant ? (parseFloat(variantForm.opening_balance) || 0) : undefined,
      };
      if (editingVariant) {
        return posApi.updateVariant({ name: editingVariant.name, ...payload });
      }
      return posApi.createVariant({ parent_item: editingItem.name, ...payload });
    },
    onSuccess: () => {
      setVariantForm({ variant_name: '', price: '', cost_price: '', unit_of_measurement: '', barcode: '', wholesale_price: '', reseller_price: '', minimum_price: '', opening_balance: '' });
      setEditingVariant(null);
      setShowVariantForm(false);
      setVariantError('');
      refetchVariants();
      qc.invalidateQueries({ queryKey: ['items'] });
    },
    onError: (err: Error) => setVariantError(err.message || 'Failed to save variant'),
  });

  const variantDeleteMutation = useMutation({
    mutationFn: (name: string) => posApi.deleteVariant(name),
    onSuccess: () => {
      refetchVariants();
      qc.invalidateQueries({ queryKey: ['items'] });
      setDeleteConfirm(null);
    },
    onError: (err: any) => {
      const errMsg = getApiErrorMessage(err, 'Failed to delete variant');
      setDeleteConfirm((prev) => prev ? { ...prev, errorMessage: errMsg } : null);
    },
  });

  const handleDeleteVariant = async (id: string, label: string, hasStock: boolean) => {
    if (hasStock) {
      setDeleteConfirm({
        id,
        label,
        type: 'variant',
        errorMessage: `This variant cannot be deleted because it has active stock quantity in hand.`
      });
      return;
    }
    try {
      await posApi.deleteVariant(id, 1);
      setDeleteConfirm({ id, label, type: 'variant' });
    } catch (err: any) {
      const errMsg = getApiErrorMessage(err, 'Cannot delete variant due to linked transaction history.');
      setDeleteConfirm({
        id,
        label,
        type: 'variant',
        errorMessage: errMsg
      });
    }
  };

  const handleCategorySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    categoryMutation.mutate(categoryFormData);
  };

  const handleBrandSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!brandFormData.brand_name.trim()) {
      setBrandError('Brand name is required');
      return;
    }
    setBrandError('');
    brandMutation.mutate(brandFormData);
  };

  const handleModelSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!modelFormData.model_name.trim()) {
      setModelError('Model name is required');
      return;
    }
    setModelError('');
    modelMutation.mutate(modelFormData);
  };

  const openCatalogModal = (type: 'category' | 'brand' | 'model') => {
    if (type === 'category') {
      setCategoryFormData({ category_name: '', description: '', category_type: 'Product' });
      setShowCategoryModal(true);
    } else if (type === 'brand') {
      setBrandFormData({ brand_name: '', description: '' });
      setBrandError('');
      setShowBrandModal(true);
    } else {
      setModelFormData({ model_name: '', description: '' });
      setModelError('');
      setShowModelModal(true);
    }
  };

  const resetForm = () => {
    const defaultTax = getDefaultVatRate(posSettings?.country);
    setFormData({
      item_name: '', item_code: '', item_type: 'Product' as 'Product' | 'Service',
      category: '', brand: '', custom_model: '', sale_price: '', cost_price: '', unit_of_measurement: '',
      barcode: '', has_variants: false, allow_fractional_qty: true, maintain_stock: true,
      is_weighted: false, plu_code: '', track_batches: false, track_serials: false,
      image: '', wholesale_price: '', reseller_price: '', minimum_price: '', opening_balance: '',
      tax_rate: defaultTax > 0 ? String(defaultTax) : '',
    });
    setVariantForm({ variant_name: '', price: '', cost_price: '', unit_of_measurement: '', barcode: '', wholesale_price: '', reseller_price: '', minimum_price: '', opening_balance: '' });
    setCustomFieldValues({});
    setEditingVariant(null);
    setShowVariantForm(false);
    setVariantError('');
    setEditingItem(null);
    setFormError('');
  };

  const openEdit = (item: POSItem) => {
    setEditingItem(item);
    setFormData({
      item_name: item.item_name,
      item_code: item.item_code,
      item_type: item.item_type as 'Product' | 'Service',
      category: item.category,
      brand: item.brand || '',
      custom_model: item.custom_model || '',
      sale_price: String(item.sale_price),
      wholesale_price: item.wholesale_price !== undefined && item.wholesale_price !== null ? String(item.wholesale_price) : '',
      reseller_price: item.reseller_price !== undefined && item.reseller_price !== null ? String(item.reseller_price) : '',
      minimum_price: item.minimum_price !== undefined && item.minimum_price !== null ? String(item.minimum_price) : '',
      cost_price: item.cost_price ? String(item.cost_price) : '',
      unit_of_measurement: item.unit_of_measurement || '',
      barcode: item.barcode || '',
      has_variants: !!item.has_variants,
      allow_fractional_qty: !!item.allow_fractional_qty,
      maintain_stock: !!item.maintain_stock,
      is_weighted: !!item.is_weighted,
      plu_code: item.plu_code || '',
      track_batches: !!item.track_batches,
      track_serials: !!item.track_serials,
      image: item.image || '',
      opening_balance: '',
      tax_rate: item.tax_rate !== undefined && item.tax_rate !== null ? String(item.tax_rate) : '',
    });
    setCustomFieldValues(item.custom_fields_data || {});
    setShowModal(true);
    setFormError('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (showVariantForm) {
      if (!confirm("You have unsaved changes in the variant edit form. Click Cancel to return and save your changes, or click OK to discard them and update the item.")) {
        return;
      }
    }
    if (!formData.category) {
      setFormError('Category is mandatory');
      return;
    }
    if (!formData.unit_of_measurement) {
      setFormError('Unit of Measurement is mandatory');
      return;
    }
    const sale = formData.sale_price ? parseFloat(formData.sale_price) : 0;
    const cost = formData.cost_price ? parseFloat(formData.cost_price) : 0;
    if (!formData.has_variants && (isNaN(sale) || sale < 0)) {
      setFormError('Sale price cannot be negative');
      return;
    }
    if (isNaN(cost) || cost < 0) {
      setFormError('Cost price cannot be negative');
      return;
    }
    if (!formData.has_variants && cost > sale) {
      setFormError('Cost price cannot be greater than sale price');
      return;
    }
    const { opening_balance: openingBalanceRaw, item_code: _existingCode, ...formFields } = formData;
    const openingBalance = parseFloat(openingBalanceRaw) || 0;
    if (!editingItem && openingBalance < 0) {
      setFormError('Opening balance cannot be negative');
      return;
    }
    const payload: Record<string, unknown> = {
      ...formFields,
      sale_price: formData.has_variants ? 0 : sale,
      wholesale_price: formData.has_variants ? 0 : (parseFloat(formData.wholesale_price) || 0),
      reseller_price: formData.has_variants ? 0 : (parseFloat(formData.reseller_price) || 0),
      minimum_price: formData.has_variants ? 0 : (parseFloat(formData.minimum_price) || 0),
      cost_price: cost,
      tax_rate: Math.max(0, parseFloat(formData.tax_rate) || 0),
      has_variants: formData.has_variants ? 1 : 0,
      allow_fractional_qty: formData.track_serials
        ? 0
        : (formData.allow_fractional_qty || formData.is_weighted ? 1 : 0),
      maintain_stock: formData.maintain_stock ? 1 : 0,
      is_weighted: formData.track_serials ? 0 : (formData.is_weighted ? 1 : 0),
      plu_code: formData.is_weighted && !formData.track_serials ? (formData.plu_code.trim() || undefined) : undefined,
      track_batches: formData.track_serials ? 0 : (formData.track_batches ? 1 : 0),
      track_serials: formData.track_serials ? 1 : 0,
      barcode: formData.has_variants ? undefined : (formData.barcode.trim() || undefined),
      custom_fields_data: customFieldValues,
    };
    // item_code: create → omit (server generates); update → omit (server preserves)
    // Offline create needs a provisional local code
    if (!editingItem && isLocalFirstMode()) {
      const companyCode = localStorage.getItem('company_code') || 'LOCAL';
      payload.item_code = `${companyCode}-${Date.now().toString(36).toUpperCase()}`;
    }
    if (!editingItem && openingBalance > 0) {
      payload.opening_balance = openingBalance;
    }
    saveMutation.mutate(payload);
  };

  const startEditVariant = (variant: ItemVariant) => {
    setEditingVariant(variant);
    setShowVariantForm(true);
    setVariantForm({
      variant_name: variant.variant_name,
      price: String(variant.price),
      wholesale_price: variant.wholesale_price !== undefined && variant.wholesale_price !== null ? String(variant.wholesale_price) : '',
      reseller_price: variant.reseller_price !== undefined && variant.reseller_price !== null ? String(variant.reseller_price) : '',
      minimum_price: variant.minimum_price !== undefined && variant.minimum_price !== null ? String(variant.minimum_price) : '',
      cost_price: String(variant.cost_price || ''),
      unit_of_measurement: variant.unit_of_measurement || '',
      barcode: variant.barcode || '',
      opening_balance: '',
    });
    setVariantError('');
  };

  const cancelVariantForm = () => {
    setEditingVariant(null);
    setShowVariantForm(false);
    setVariantForm({ variant_name: '', price: '', cost_price: '', unit_of_measurement: '', barcode: '', wholesale_price: '', reseller_price: '', minimum_price: '', opening_balance: '' });
    setVariantError('');
  };

  const openPrintLabels = async (item: POSItem) => {
    if (item.has_variants) {
      const res = await posApi.getItemVariants(item.name);
      setPrintVariants((res.data.message || []) as ItemVariant[]);
    } else {
      setPrintVariants([]);
    }
    setPrintTarget(item);
  };

  const handleGenerateItemBarcode = async () => {
    if (!editingItem) {
      setFormError('Save the item first, then generate a barcode from item code.');
      return;
    }
    try {
      const res = await posApi.generateItemBarcode(editingItem.name);
      const code = res.data.message?.barcode as string;
      setFormData((prev) => ({ ...prev, barcode: code }));
      qc.invalidateQueries({ queryKey: ['items'] });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setFormError(typeof msg === 'string' ? msg : 'Could not generate barcode');
    }
  };

  const handleGenerateVariantBarcode = async (variantId?: string) => {
    const id = variantId || editingVariant?.name;
    if (!id) {
      setVariantError('Save the variant first, then generate a barcode.');
      return;
    }
    try {
      const res = await posApi.generateVariantBarcode(id);
      const code = res.data.message?.barcode as string;
      if (editingVariant?.name === id) {
        setVariantForm((prev) => ({ ...prev, barcode: code }));
      }
      refetchVariants();
      qc.invalidateQueries({ queryKey: ['items'] });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setVariantError(typeof msg === 'string' ? msg : 'Could not generate barcode');
    }
  };

  const openStockModal = (item: POSItem, type: 'in' | 'out') => {
    setSelectedStockItem(item);
    setStockModalType(type);
    setStockFormData({
      supplier: '',
      purchase_date: new Date().toISOString().split('T')[0],
      unit_price: item.cost_price ? String(item.cost_price) : '',
      quantity: '',
      stock_out_type: 'Damage/Loss',
      variant: '',
      batch_no: '',
      expiry_date: '',
      serials: [],
    });
  };

  const syncSerialRows = (qtyStr: string) => {
    const qty = Math.max(0, Math.floor(parseFloat(qtyStr) || 0));
    setStockFormData((prev) => {
      const next = [...prev.serials];
      while (next.length < qty) next.push({ imei: '', mac_address: '' });
      while (next.length > qty) next.pop();
      return { ...prev, quantity: qtyStr, serials: next };
    });
  };

  const buildStockPayload = () => {
    const payload: Record<string, unknown> = {
      item: selectedStockItem!.name,
      transaction_date: stockFormData.purchase_date,
      quantity: parseFloat(stockFormData.quantity),
      unit_price: parseFloat(stockFormData.unit_price) || 0,
    };
    if (selectedStockItem?.has_variants) {
      if (!stockFormData.variant) throw new Error('Select a variant');
      payload.variant = stockFormData.variant;
    }
    if (selectedStockItem?.track_batches) {
      if (!stockFormData.batch_no.trim()) throw new Error('Batch number is required');
      if (!stockFormData.expiry_date) throw new Error('Expiry date is required');
      payload.batch_no = stockFormData.batch_no.trim();
      payload.expiry_date = stockFormData.expiry_date;
    }
    if (selectedStockItem?.track_serials) {
      const qty = Math.floor(parseFloat(stockFormData.quantity) || 0);
      if (qty <= 0) throw new Error('Quantity must be a positive integer');
      if (stockFormData.serials.length !== qty) {
        throw new Error(`Enter exactly ${qty} IMEI(s)`);
      }
      const seen = new Set<string>();
      const serials = stockFormData.serials.map((row) => {
        const imei = row.imei.trim();
        if (!imei) throw new Error('Each row requires an IMEI');
        if (seen.has(imei)) throw new Error(`Duplicate IMEI: ${imei}`);
        seen.add(imei);
        return { imei, mac_address: row.mac_address.trim() || undefined };
      });
      payload.serials = serials;
      payload.quantity = qty;
    }
    return payload;
  };

  const handleStockSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStockItem) return;

    try {
      if (stockModalType === 'in') {
        if (!stockFormData.supplier) throw new Error('Select a supplier');
        stockInMutation.mutate({
          ...buildStockPayload(),
          supplier: stockFormData.supplier,
        });
      } else {
        if (stockFormData.stock_out_type === 'Return To Supplier' && !stockFormData.supplier) {
          throw new Error('Select a supplier for return');
        }
        const payload: Record<string, unknown> = {
          ...buildStockPayload(),
          stock_out: stockFormData.stock_out_type,
        };
        if (stockFormData.stock_out_type === 'Return To Supplier') {
          payload.supplier = stockFormData.supplier;
        }
        stockOutMutation.mutate(payload);
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Invalid stock form');
    }
  };

  const items = itemsData?.items || [];
  const itemsTotal = itemsData?.total || 0;
  const categories = categoriesData || [];
  const isStockPending = stockInMutation.isPending || stockOutMutation.isPending;

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <TrialBanner />
        {stockInNotice && (
          <div className="mx-6 mt-4 bg-emerald-50 border border-emerald-100 text-emerald-800 text-sm px-4 py-2 rounded-lg">
            {stockInNotice}
          </div>
        )}
        <div className="p-6">
          <div className="mb-6 space-y-4">
            <div className="flex items-center gap-3">
              <button onClick={toggleSidebar} className="lg:hidden p-2 -ml-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg shrink-0">
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{t('items.title')}</h1>
                <p className="text-gray-500 text-sm">{t('items.subtitle')}</p>
                {productLimits && productLimits.max != null && (
                  <p className="text-xs text-gray-400 mt-1">
                    Products: {productLimits.current} / {productLimits.max}
                  </p>
                )}
              </div>
            </div>

            <ItemsPageToolbar
              canManagePrices={canManagePrices}
              isAdmin={isCompanyAdmin(role)}
              brandModelVisible={brandModelVisible}
              canAddProducts={productLimits?.can_add_more !== false}
              onAddItem={() => { resetForm(); setShowModal(true); }}
              onAddCategory={() => openCatalogModal('category')}
              onAddBrand={() => openCatalogModal('brand')}
              onAddModel={() => openCatalogModal('model')}
              onUpdatePrices={() => setShowBulkPriceModal(true)}
              onBulkUpdateExcel={() => setShowBulkProductUpdateModal(true)}
              onExportCatalog={() => setShowCatalogExportModal(true)}
              onImport={() => setShowImportChooser(true)}
              onBulkPrint={() => setShowBulkPrintModal(true)}
              onCustomFields={() => setShowCustomFieldsModal(true)}
            />
          </div>
          {productLimits?.can_add_more === false && (
            <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-2 rounded-lg">
              Product limit reached for your package. Upgrade your plan to add more products.
            </div>
          )}

          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-6">
            <button
              onClick={() => setActiveTab('Products List')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'Products List'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('items.productsList')}
            </button>
            <button
              onClick={() => setActiveTab('Stock Report')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'Stock Report'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('items.stockReport')}
            </button>
          </div>

          {activeTab === 'Stock Report' ? (
            <StockReportTab />
          ) : (
            <>
              {/* Filters */}
          <div className="card p-4 mb-4 flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text" placeholder={t('items.searchPlaceholder')}
                className="input-field pl-9" value={search} onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            
            <div className="w-full sm:w-[220px]">
              <select
                className="input-field animate-fade-in"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <option value="">{t('items.allCategories')}</option>
                {filterType === 'All' ? (
                  <>
                    <optgroup label={t('nav.products')}>
                      {categoriesData?.filter((c: any) => c.category_type === 'Product').map((c: any) => (
                        <option key={c.name} value={c.name}>{c.category_name}</option>
                      ))}
                    </optgroup>
                    <optgroup label={t('pos.services')}>
                      {categoriesData?.filter((c: any) => c.category_type === 'Service').map((c: any) => (
                        <option key={c.name} value={c.name}>{c.category_name}</option>
                      ))}
                    </optgroup>
                  </>
                ) : (
                  categoriesData?.filter((c: any) => c.category_type === filterType).map((c: any) => (
                    <option key={c.name} value={c.name}>{c.category_name}</option>
                  ))
                )}
              </select>
            </div>

            {!posSettings?.sell_only_products && (
              <div className="flex gap-2">
                {(['All', 'Product', 'Service'] as const).map((typeFilter) => (
                  <button key={typeFilter} onClick={() => setFilterType(typeFilter)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filterType === typeFilter ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}>
                    {typeFilter === 'All' ? t('common.all') : typeFilter === 'Product' ? t('items.product') : t('items.service')}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Items Table Layout */}
          <div className="card overflow-hidden border border-gray-100 shadow-sm rounded-xl">
            {isLoading ? (
              <div className="p-8 text-center text-gray-400">{t('items.loading')}</div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>{t('items.empty')}</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto hide-scrollbar">
                  <table className="w-full text-sm layout-fixed min-w-[800px]">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr>
                        <th className="text-left px-4 py-3.5 font-medium text-gray-600 w-[22%]">{t('common.item')}</th>
                        <th className="text-left px-4 py-3.5 font-medium text-gray-600 w-[16%]">{t('common.code')}</th>
                        <th className="text-left px-4 py-3.5 font-medium text-gray-600 w-[10%]">{t('common.type')}</th>
                        <th className="text-left px-4 py-3.5 font-medium text-gray-600 w-[16%]">{t('common.category')}</th>
                        <th className="text-right px-4 py-3.5 font-medium text-gray-600 w-[12%]">{t('common.price')}</th>
                        <th className="text-right px-6 py-3.5 font-medium text-gray-600 w-[16%]">{t('items.stockStatus')}</th>
                        <th className="text-right px-4 py-3.5 font-medium text-gray-600 w-[8%]">{t('common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {items.map((item) => (
                        <tr key={item.name} className="hover:bg-gray-50/70 transition-colors">
                           <td className="px-4 py-3.5">
                            <div className="flex items-center gap-3">
                              {item.image ? (
                                <img
                                  src={resolveFileUrl(item.image)}
                                  alt={item.item_name}
                                  className="w-8 h-8 rounded-lg shrink-0 object-cover border border-gray-100"
                                />
                              ) : (
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${item.item_type === 'Product' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'
                                  }`}>
                                  <Package className="w-4 h-4" />
                                </div>
                              )}
                              <span className="font-medium text-gray-900 truncate">{item.item_name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3.5 text-gray-500 font-mono text-xs">{item.item_code}</td>
                          <td className="px-4 py-3.5">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${item.item_type === 'Product' ? 'bg-blue-50 text-blue-700 border border-blue-100' : 'bg-purple-50 text-purple-700 border border-purple-100'
                              }`}>{item.item_type}</span>
                          </td>
                          <td className="px-4 py-3.5 text-gray-500 truncate">
                            {categories.find((c: any) => c.name === item.category)?.category_name || item.category || '—'}
                          </td>
                          <td className="px-4 py-3.5 text-right">
                            {item.has_variants ? (
                              <div className="group relative inline-block">
                                <span className="text-emerald-600 hover:text-emerald-700 cursor-help border-b border-dashed border-emerald-300 font-semibold">Variant</span>
                                <div className="absolute right-0 bottom-full mb-2 hidden group-hover:block w-max min-w-[140px] bg-gray-900 text-white text-xs rounded-lg shadow-xl p-3 z-10 text-left pointer-events-none">
                                  {item.variants && item.variants.length > 0 ? (
                                    <ul className="space-y-1.5">
                                      {item.variants.map((v) => (
                                        <li key={v.name} className="flex gap-4 justify-between items-center">
                                          <span className="text-gray-300 font-normal">{v.variant_name}</span>
                                          <span className="font-semibold text-emerald-400">{formatMoney(v.price)}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <span className="text-gray-400 font-normal italic">{t('items.noVariants')}</span>
                                  )}
                                  <div className="absolute top-full right-4 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                                </div>
                              </div>
                            ) : (
                              <span className="text-emerald-600 font-semibold">{formatMoney(item.sale_price)}</span>
                            )}
                          </td>
                          {/* Integrated Stock Action Column */}
                          <td className="px-6 py-3.5 text-right align-top">
                            {item.item_type === 'Product' && !!item.maintain_stock ? (
                              <div className="flex flex-col items-end gap-1 group/stock">
                                <div className="flex items-center justify-end gap-3">
                                  <span className={`text-sm font-semibold px-2 py-0.5 rounded ${item.stock_quantity !== undefined && item.stock_quantity > 5
                                    ? 'bg-gray-100 text-gray-800'
                                    : 'bg-red-50 text-red-600 font-bold'
                                    }`}>
                                    {item.stock_quantity || 0}
                                  </span>
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => openStockModal(item, 'in')}
                                      className="px-1.5 py-0.5 text-[10px] font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded transition-colors"
                                      title={t('items.stockIn')}
                                    >
                                      + {t('items.stockIn')}
                                    </button>
                                    <button
                                      onClick={() => openStockModal(item, 'out')}
                                      className="px-1.5 py-0.5 text-[10px] font-bold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded transition-colors"
                                      title={t('items.stockOut')}
                                    >
                                      - {t('items.stockOut')}
                                    </button>
                                  </div>
                                </div>
                                <StockIdentityPreview
                                  itemName={item.name}
                                  trackBatches={item.track_batches}
                                  trackSerials={item.track_serials}
                                  stockQuantity={item.stock_quantity || 0}
                                />
                              </div>
                            ) : (
                              <span className="text-gray-300 pr-4">—</span>
                            )}
                          </td>

                          {/* Balanced Actions Column */}
                          <td className="px-4 py-3.5 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                type="button"
                                onClick={() => openPrintLabels(item)}
                                className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                title="Print barcode labels"
                              >
                                <Printer className="w-4 h-4" />
                              </button>
                              <button onClick={() => openEdit(item)} className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors" title="Edit product">
                                <Pencil className="w-4 h-4" />
                              </button>
                              {isCompanyAdmin(role) && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteItem(item.name, item.item_name, Boolean(item.stock_quantity && item.stock_quantity > 0))}
                                  className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete product"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={page}
                  pageSize={LIST_PAGE_SIZE}
                  total={itemsTotal}
                  onPageChange={setPage}
                />
              </>
            )}
          </div>
            </>
          )}
        </div>
      </div>

      {/* Add / Edit Base Item Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 shrink-0">
              <h3 className="font-semibold text-gray-900">{editingItem ? t('items.editItem') : t('items.addNewItem')}</h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            {formError && <div className="bg-rose-50 border border-rose-200 text-rose-800 text-sm px-5 py-2 shrink-0">{formError}</div>}
            {editingItem && formData.item_type === 'Product' && (
              <div className="mx-5 mt-4 bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 flex items-center justify-between shrink-0">
                <span className="text-sm font-medium text-blue-800">Current stock</span>
                <span className="text-lg font-bold text-blue-900">
                  {editStockBalance ?? editingItem.stock_quantity ?? 0}
                  {formData.unit_of_measurement ? ` ${formData.unit_of_measurement}` : ''}
                </span>
              </div>
            )}
            <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Item Name</label>
                  <input required className="input-field" value={formData.item_name} onChange={(e) => setFormData({ ...formData, item_name: e.target.value })} />
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm font-medium text-gray-700">Item Code:</span>
                    <span className="text-sm text-gray-900 font-mono">
                      {editingItem
                        ? (formData.item_code || '—')
                        : (formData.item_code || 'Auto-generated on save')}
                    </span>
                  </div>
                </div>
                {!posSettings?.sell_only_products && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select className="input-field" value={formData.item_type} onChange={(e) => setFormData({ ...formData, item_type: e.target.value as 'Product' | 'Service' })}>
                      <option value="Product">Product</option>
                      <option value="Service">Service</option>
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select className="input-field" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })}>
                    <option value="">Select...</option>
                    {categoriesData?.filter((c: any) => c.category_type === formData.item_type).map((c: any) => (
                      <option key={c.name} value={c.name}>{c.category_name}</option>
                    ))}
                  </select>
                </div>
                {brandModelVisible && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
                      <select className="input-field" value={formData.brand} onChange={(e) => setFormData({ ...formData, brand: e.target.value })}>
                        <option value="">Select...</option>
                        {brandsData?.map((b: any) => (
                          <option key={b.name} value={b.name}>{b.brand_name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                      <select className="input-field" value={formData.custom_model} onChange={(e) => setFormData({ ...formData, custom_model: e.target.value })}>
                        <option value="">Select...</option>
                        {modelsData?.map((m: any) => (
                          <option key={m.name} value={m.name}>{m.model_name}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit of Measure</label>
                  <select className="input-field" value={formData.unit_of_measurement} onChange={(e) => setFormData({ ...formData, unit_of_measurement: e.target.value })}>
                    <option value="" disabled>Select UOM</option>
                    {uomData?.map((u: any) => (
                      <option key={u.name} value={u.name}>{u.unit_of_measure}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.has_variants}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setFormData({
                          ...formData,
                          has_variants: checked,
                          ...(checked ? { sale_price: '', cost_price: '' } : {})
                        });
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-primary-600"
                    />
                    <span className="text-sm font-medium text-gray-700">Has variants (sell by variant on POS)</span>
                  </label>
                </div>
                {formData.item_type === 'Product' && (
                  <div className="col-span-2 rounded-lg border border-gray-100 bg-gray-50/80 p-3 space-y-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('items.inventoryRules')}</p>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.allow_fractional_qty || formData.is_weighted}
                        disabled={formData.is_weighted || formData.track_serials}
                        onChange={(e) => setFormData({ ...formData, allow_fractional_qty: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-300 text-primary-600"
                      />
                      <span className="text-sm font-medium text-gray-700">{t('items.fractionUom')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_weighted}
                        disabled={formData.track_serials}
                        onChange={(e) => setFormData({
                          ...formData,
                          is_weighted: e.target.checked,
                          allow_fractional_qty: e.target.checked ? true : formData.allow_fractional_qty,
                          track_serials: e.target.checked ? false : formData.track_serials,
                        })}
                        className="w-4 h-4 rounded border-gray-300 text-primary-600"
                      />
                      <span className="text-sm font-medium text-gray-700">{t('items.sellByWeight')}</span>
                    </label>
                    {formData.is_weighted && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('items.pluCode')}</label>
                        <input
                          className="input-field font-mono"
                          value={formData.plu_code}
                          onChange={(e) => setFormData({ ...formData, plu_code: e.target.value })}
                          placeholder="4011"
                        />
                      </div>
                    )}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.track_batches}
                        disabled={formData.track_serials}
                        onChange={(e) => setFormData({
                          ...formData,
                          track_batches: e.target.checked,
                          track_serials: e.target.checked ? false : formData.track_serials,
                        })}
                        className="w-4 h-4 rounded border-gray-300 text-primary-600"
                      />
                      <span className="text-sm font-medium text-gray-700">{t('items.trackBatches')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.track_serials}
                        onChange={(e) => setFormData({
                          ...formData,
                          track_serials: e.target.checked,
                          track_batches: e.target.checked ? false : formData.track_batches,
                          is_weighted: e.target.checked ? false : formData.is_weighted,
                          allow_fractional_qty: e.target.checked ? false : formData.allow_fractional_qty,
                        })}
                        className="w-4 h-4 rounded border-gray-300 text-primary-600"
                      />
                      <span className="text-sm font-medium text-gray-700">{t('items.trackSerials')}</span>
                    </label>
                    {formData.track_serials && (
                      <p className="text-xs text-amber-700">{t('items.serialOnlineOnly')}</p>
                    )}
                  </div>
                )}
                {!formData.has_variants && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cost Price ({getCurrencySymbol()})</label>
                      <input type="number" min="0" step="0.01" className="input-field" value={formData.cost_price} onChange={(e) => setFormData({ ...formData, cost_price: e.target.value })} onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Retail Price ({getCurrencySymbol()}) *</label>
                      <input required type="number" min="0" step="0.01" className="input-field" value={formData.sale_price} onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })} onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Wholesale Price ({getCurrencySymbol()})</label>
                      <input type="number" min="0" step="0.01" className="input-field" value={formData.wholesale_price} onChange={(e) => setFormData({ ...formData, wholesale_price: e.target.value })} onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Reseller Price ({getCurrencySymbol()})</label>
                      <input type="number" min="0" step="0.01" className="input-field" value={formData.reseller_price} onChange={(e) => setFormData({ ...formData, reseller_price: e.target.value })} onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Selling Price ({getCurrencySymbol()})</label>
                      <input type="number" min="0" step="0.01" className="input-field" value={formData.minimum_price} onChange={(e) => setFormData({ ...formData, minimum_price: e.target.value })} onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }} />
                    </div>
                  </>
                )}
                <div className={formData.has_variants ? 'col-span-2' : ''}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tax Rate (%)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="input-field"
                    value={formData.tax_rate}
                    onChange={(e) => setFormData({ ...formData, tax_rate: e.target.value })}
                    onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('items.taxRateHelp', { rate: getDefaultVatRate(posSettings?.country) || 0 })}
                  </p>
                </div>
                {!formData.has_variants && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Barcode</label>
                    <div className="flex gap-2">
                      <input
                        className="input-field flex-1 font-mono text-sm"
                        placeholder="Factory barcode or scan gun"
                        value={formData.barcode}
                        onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                      />
                      <button
                        type="button"
                        onClick={handleGenerateItemBarcode}
                        className="btn-secondary text-xs px-3 shrink-0 whitespace-nowrap"
                        title="Use item code as barcode"
                      >
                        Use item code
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">One barcode for all pieces in stock.</p>
                  </div>
                )}

                {formData.item_type === 'Product' && (
                  <div className="col-span-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.maintain_stock}
                        disabled={!!editingItem?.has_stock_ledger && formData.maintain_stock}
                        onChange={(e) => setFormData({ ...formData, maintain_stock: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-300 text-primary-600 disabled:opacity-60"
                      />
                      <span className="text-sm font-medium text-gray-700">Maintain Stock (Enable stock management)</span>
                    </label>
                    {!!editingItem?.has_stock_ledger && formData.maintain_stock && (
                      <p className="text-xs text-amber-600 mt-1 ml-6">
                        Cannot disable — stock ledger entries exist for this item.
                      </p>
                    )}
                  </div>
                )}
                {!editingItem && formData.item_type === 'Product' && formData.maintain_stock && !formData.has_variants && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Opening Balance (optional)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="input-field"
                      placeholder="0"
                      value={formData.opening_balance}
                      onChange={(e) => setFormData({ ...formData, opening_balance: e.target.value })}
                      onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                    />
                  </div>
                )}

                <div className="col-span-2 border-t border-gray-150 pt-4 mt-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Product Image</label>
                  <div className="flex items-center gap-4">
                    {formData.image ? (
                      <div className="relative w-16 h-16 rounded-lg border border-gray-200 overflow-hidden bg-gray-50 flex-shrink-0">
                        <img src={resolveFileUrl(formData.image)} alt="Preview" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, image: '' })}
                          className="absolute inset-0 bg-black/50 text-white flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity text-xs font-semibold"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 bg-gray-50 flex-shrink-0">
                        <Upload className="w-6 h-6" />
                      </div>
                    )}
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        id="product-image-upload"
                        className="hidden"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                      />
                      <label
                        htmlFor="product-image-upload"
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg shadow-sm text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        {uploadingImage ? 'Uploading...' : formData.image ? 'Change Image' : 'Select Image'}
                      </label>
                      <p className="text-[10px] text-gray-500 mt-1">Images will be resized to a max of 800x800px automatically.</p>
                    </div>
                  </div>
                </div>
              </div>

              {customFields.length > 0 && (
                <div className="border border-gray-100 rounded-lg p-4 space-y-3">
                  <h4 className="text-sm font-semibold text-gray-900">Custom Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {customFields.map((field) => (
                      <div key={field.fieldname} className={customFields.length === 1 ? 'col-span-2' : ''}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {field.label}
                          {!!field.is_unique && <span className="text-xs text-gray-400 ml-1">(unique)</span>}
                        </label>
                        {field.field_type === 'Select' ? (
                          <select
                            className="input-field"
                            value={customFieldValues[field.fieldname] || ''}
                            onChange={(event) => setCustomFieldValues({
                              ...customFieldValues,
                              [field.fieldname]: event.target.value,
                            })}
                          >
                            <option value="">Select...</option>
                            {(field.options || '').split(/\r?\n/).filter(Boolean).map((option) => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            className="input-field"
                            value={customFieldValues[field.fieldname] || ''}
                            onChange={(event) => setCustomFieldValues({
                              ...customFieldValues,
                              [field.fieldname]: event.target.value,
                            })}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {formData.has_variants && editingItem && (
                <div className="border border-gray-100 rounded-lg p-4 space-y-3 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-900">Variants</h4>
                    <button
                      type="button"
                      onClick={() => {
                        cancelVariantForm();
                        setShowVariantForm(true);
                        setVariantForm({ variant_name: '', price: '', cost_price: '', unit_of_measurement: formData.unit_of_measurement || '', barcode: '', wholesale_price: '', reseller_price: '', minimum_price: '', opening_balance: '' });
                      }}
                      className="bg-primary-600 text-white hover:bg-primary-700 text-xs py-1.5 px-3 flex items-center gap-1 rounded-lg transition-colors font-medium shadow-sm"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Variant
                    </button>
                  </div>
                  {variantError && <p className="text-xs text-red-600">{variantError}</p>}
                  {showVariantForm && (
                    <div className="grid grid-cols-2 gap-3 bg-white border border-gray-100 rounded-lg p-3">
                      <div className="col-span-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Variant name</label>
                        <input className="input-field text-sm" value={variantForm.variant_name} onChange={(e) => setVariantForm({ ...variantForm, variant_name: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Cost Price ({getCurrencySymbol()})</label>
                        <input type="number" min="0" step="0.01" className="input-field text-sm" value={variantForm.cost_price} onChange={(e) => setVariantForm({ ...variantForm, cost_price: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Retail Price ({getCurrencySymbol()})</label>
                        <input type="number" min="0" step="0.01" className="input-field text-sm" value={variantForm.price} onChange={(e) => setVariantForm({ ...variantForm, price: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Wholesale Price ({getCurrencySymbol()})</label>
                        <input type="number" min="0" step="0.01" className="input-field text-sm" value={variantForm.wholesale_price} onChange={(e) => setVariantForm({ ...variantForm, wholesale_price: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Reseller Price ({getCurrencySymbol()})</label>
                        <input type="number" min="0" step="0.01" className="input-field text-sm" value={variantForm.reseller_price} onChange={(e) => setVariantForm({ ...variantForm, reseller_price: e.target.value })} />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Minimum Price ({getCurrencySymbol()})</label>
                        <input type="number" min="0" step="0.01" className="input-field text-sm" value={variantForm.minimum_price} onChange={(e) => setVariantForm({ ...variantForm, minimum_price: e.target.value })} />
                      </div>
                      {!editingVariant && formData.maintain_stock && (
                        <div className="col-span-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Opening Balance (optional)</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="input-field text-sm"
                            placeholder="0"
                            value={variantForm.opening_balance}
                            onChange={(e) => setVariantForm({ ...variantForm, opening_balance: e.target.value })}
                            onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                          />
                        </div>
                      )}
                      <div className="col-span-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Barcode</label>
                        <div className="flex gap-2">
                          <input
                            className="input-field text-sm flex-1 font-mono"
                            placeholder="Option barcode"
                            value={variantForm.barcode}
                            onChange={(e) => setVariantForm({ ...variantForm, barcode: e.target.value })}
                          />
                          {editingVariant && (
                            <button
                              type="button"
                              onClick={() => handleGenerateVariantBarcode(editingVariant.name)}
                              className="btn-secondary text-xs px-2 shrink-0"
                            >
                              Use code
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="col-span-2 flex justify-end gap-2 mt-2">
                        <button type="button" onClick={cancelVariantForm} className="btn-secondary text-xs py-1.5 px-4">Cancel</button>
                        <button type="button" disabled={variantSaveMutation.isPending} onClick={() => variantSaveMutation.mutate()} className="btn-primary text-xs py-1.5 px-4">
                          {variantSaveMutation.isPending ? 'Saving...' : editingVariant ? 'Update' : 'Add'}
                        </button>
                      </div>
                    </div>
                  )}
                  {itemVariants.length > 0 ? (
                    <div className="grid grid-cols-1 gap-2.5">
                      {itemVariants.map((v) => (
                        <div key={v.name} className="flex items-center justify-between p-3.5 bg-slate-50/50 hover:bg-slate-50 border border-slate-200/80 rounded-xl transition-all shadow-sm">
                          <div className="space-y-1.5 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-900 text-sm truncate">{v.variant_name}</span>
                              {v.barcode && (
                                <span className="text-[10px] bg-gray-100 text-gray-500 font-mono px-2 py-0.5 rounded-md border border-gray-200">
                                  {v.barcode}
                                </span>
                              )}
                              {formData.item_type === 'Product' && (
                                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${v.stock_quantity && v.stock_quantity > 0 ? 'bg-emerald-50 text-emerald-700 border border-emerald-100/50' : 'bg-rose-50 text-rose-700 border border-rose-100/50'}`}>
                                  {v.stock_quantity ?? 0} in stock
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1.5 items-center">
                            <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100/60" title="Retail Price">
                                Retail: {formatMoney(v.price)}
                              </span>
                              <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-100/60" title="Wholesale Price">
                                Wholesale: {formatMoney((v.wholesale_price || 0))}
                              </span>
                              <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-100/60" title="Reseller Price">
                                Reseller: {formatMoney((v.reseller_price || 0))}
                              </span>
                              <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-100/60" title="Minimum Price">
                                Min: {formatMoney((v.minimum_price || 0))} / {v.unit_of_measurement}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-1.5 shrink-0 ml-4">
                            {!v.barcode && (
                              <button
                                type="button"
                                onClick={() => handleGenerateVariantBarcode(v.name)}
                                className="p-2 text-primary-700 hover:text-white bg-white hover:bg-primary-600 border border-gray-200 hover:border-primary-600 rounded-lg transition-all flex items-center justify-center shadow-sm"
                                title="Generate barcode"
                              >
                                <Barcode className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => startEditVariant(v)}
                              className="p-2 text-blue-700 hover:text-white bg-blue-50 hover:bg-blue-600 border border-blue-200 hover:border-blue-600 rounded-lg transition-all flex items-center justify-center shadow-sm"
                              title="Edit variant"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            {isCompanyAdmin(role) && (
                              <button
                                type="button"
                                onClick={() => handleDeleteVariant(v.name, v.variant_name, Boolean(v.stock_quantity && v.stock_quantity > 0))}
                                className="p-2 text-red-700 hover:text-white bg-red-50 hover:bg-red-600 border border-red-200 hover:border-red-600 rounded-lg transition-all flex items-center justify-center shadow-sm"
                                title="Delete variant"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">No variants yet. Click Add Variant to create one.</p>
                  )}
                </div>
              )}

              {formData.has_variants && !editingItem && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  Save the item first, then you can add variants.
                </p>
              )}
              </div>

              <div className="shrink-0 border-t border-gray-100 bg-gray-50 p-4 flex gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">Cancel</button>
                <button type="submit" disabled={saveMutation.isPending} className="btn-primary flex-1">
                  {saveMutation.isPending ? 'Saving...' : (editingItem ? 'Update and Close' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modernized Stock Handling Adjustment Modal */}
      {stockModalType && selectedStockItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 shrink-0">
              <div>
                <h3 className="font-semibold text-gray-900 text-lg">
                  Stock {stockModalType === 'in' ? 'In (Add Inventory)' : 'Out (Reduce Inventory)'}
                </h3>
                <p className="text-gray-500 text-xs mt-0.5">Manage stock levels for your product catalog.</p>
              </div>
              <button onClick={() => { setStockModalType(null); setSelectedStockItem(null); }} className="p-1.5 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <form onSubmit={handleStockSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
              <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* Informational Read-Only Banner stays safely anchored on top */}
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3.5 flex items-center justify-between">
                <div className="text-sm text-blue-800 font-medium">
                  {selectedStockItem.has_variants
                    ? (stockFormData.variant ? 'Variant stock' : 'Total stock')
                    : 'Current Available Stock'}
                </div>
                <div className="text-xl font-bold text-blue-900 bg-white px-3 py-0.5 rounded-md border border-blue-200">
                  {selectedStockItem.has_variants && stockFormData.variant
                    ? (selectedVariantStock ?? 0)
                    : (selectedStockItem.stock_quantity || 0)}
                </div>
              </div>

              {!!selectedStockItem.has_variants && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Variant <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    className="input-field"
                    value={stockFormData.variant}
                    onChange={(e) => {
                      const next = { ...stockFormData, variant: e.target.value };
                      if (
                        stockModalType === 'out'
                        && stockFormData.stock_out_type === 'Return To Supplier'
                      ) {
                        next.supplier = '';
                        next.unit_price = '';
                      }
                      setStockFormData(next);
                    }}
                  >
                    <option value="">Select variant...</option>
                    {stockModalVariants.map((v) => (
                      <option key={v.name} value={v.name}>
                        {v.variant_name} ({v.stock_quantity ?? 0} in stock)
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {stockModalType === 'in' ? (
                /* Enhanced Stock In Layout matching grid pattern specifications */
                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Item <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      disabled
                      className="input-field bg-gray-50 text-gray-500 cursor-not-allowed font-medium"
                      value={selectedStockItem.item_name}
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Supplier <span className="text-red-500">*</span>
                    </label>
                    <SupplierSelect
                      required
                      value={stockFormData.supplier}
                      onChange={(supplier) => setStockFormData({ ...stockFormData, supplier })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Purchase Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      required
                      type="date"
                      className="input-field"
                      max={new Date().toISOString().split('T')[0]}
                      value={stockFormData.purchase_date}
                      onChange={(e) => setStockFormData({ ...stockFormData, purchase_date: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Unit Price</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="input-field"
                      placeholder={`${getCurrencySymbol()} 0.00`}
                      value={stockFormData.unit_price}
                      onChange={(e) => setStockFormData({ ...stockFormData, unit_price: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantity <span className="text-red-500">*</span>
                    </label>
                    <input
                      required
                      type="number"
                      min={selectedStockItem.allow_fractional_qty && !selectedStockItem.track_serials ? "0.01" : "1"}
                      step={selectedStockItem.allow_fractional_qty && !selectedStockItem.track_serials ? "any" : "1"}
                      className="input-field"
                      placeholder="e.g. 10"
                      value={stockFormData.quantity}
                      onChange={(e) => {
                        if (selectedStockItem.track_serials) {
                          syncSerialRows(e.target.value);
                        } else {
                          setStockFormData({ ...stockFormData, quantity: e.target.value });
                        }
                      }}
                    />
                  </div>
                  {!!selectedStockItem.track_serials && (
                    <div className="col-span-2">
                      <ImeiSerialListEditor
                        serials={stockFormData.serials}
                        quantity={Math.floor(parseFloat(stockFormData.quantity) || 0)}
                        note={t('items.serialOnlineOnly')}
                        onChange={(serials, qty) => {
                          if (qty != null) {
                            setStockFormData((prev) => ({
                              ...prev,
                              quantity: String(qty),
                              serials,
                            }));
                          } else {
                            setStockFormData((prev) => ({ ...prev, serials }));
                          }
                        }}
                      />
                    </div>
                  )}
                  {!!selectedStockItem.track_batches && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('items.batchNo')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          required
                          className="input-field font-mono"
                          value={stockFormData.batch_no}
                          onChange={(e) => setStockFormData({ ...stockFormData, batch_no: e.target.value })}
                          placeholder="BATCH-001"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t('items.expiryDate')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          required
                          type="date"
                          className="input-field"
                          value={stockFormData.expiry_date}
                          onChange={(e) => setStockFormData({ ...stockFormData, expiry_date: e.target.value })}
                        />
                      </div>
                    </>
                  )}
                </div>
              ) : (
                /* Updated Stock Out Layout matching grid specifications exactly */
                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Item <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      disabled
                      className="input-field bg-gray-50 text-gray-500 cursor-not-allowed font-medium"
                      value={selectedStockItem.item_name}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Stock Out <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      className="input-field"
                      value={stockFormData.stock_out_type}
                      onChange={(e) => setStockFormData({
                        ...stockFormData,
                        stock_out_type: e.target.value,
                        supplier: '',
                        unit_price: e.target.value === 'Return To Supplier' ? '' : stockFormData.unit_price,
                      })}
                    >
                      <option value="Damage/Loss">Damage/Loss</option>
                      <option value="Return To Supplier">Return To Supplier</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Transaction Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      required
                      type="date"
                      className="input-field"
                      max={new Date().toISOString().split('T')[0]}
                      value={stockFormData.purchase_date}
                      onChange={(e) => setStockFormData({ ...stockFormData, purchase_date: e.target.value })}
                    />
                  </div>

                  {/* Dynamic condition: Hidden unless "Return To Supplier" is picked */}
                  {stockFormData.stock_out_type === 'Return To Supplier' ? (
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Supplier <span className="text-red-500">*</span>
                      </label>
                      <SupplierSelect
                        required
                        mode="item_stock_in"
                        item={selectedStockItem.name}
                        variant={selectedStockItem.has_variants ? stockFormData.variant || undefined : undefined}
                        requiresVariant={!!selectedStockItem.has_variants}
                        allowQuickAdd={false}
                        value={stockFormData.supplier}
                        onChange={(supplier) => setStockFormData({ ...stockFormData, supplier })}
                        onSupplierPick={(s) => {
                          if (s.last_unit_price != null && s.last_unit_price > 0) {
                            setStockFormData((prev) => ({
                              ...prev,
                              unit_price: String(s.last_unit_price),
                            }));
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <div aria-hidden="true" />
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantity <span className="text-red-500">*</span>
                    </label>
                    <input
                      required
                      type="number"
                      min={selectedStockItem.allow_fractional_qty ? "0.01" : "1"}
                      step={selectedStockItem.allow_fractional_qty ? "any" : "1"}
                      max={
                        selectedStockItem.has_variants && stockFormData.variant
                          ? (selectedVariantStock ?? 0)
                          : (selectedStockItem.stock_quantity || 0)
                      }
                      className="input-field"
                      placeholder="e.g. 5"
                      value={stockFormData.quantity}
                      onChange={(e) => setStockFormData({ ...stockFormData, quantity: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {stockFormData.stock_out_type === 'Return To Supplier' ? 'Return Unit Price' : 'Unit Price'}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="input-field"
                      placeholder={`${getCurrencySymbol()} 0.00`}
                      value={stockFormData.unit_price}
                      onChange={(e) => setStockFormData({ ...stockFormData, unit_price: e.target.value })}
                    />
                  </div>

                  <div className="col-span-2">

                  </div>
                </div>
              )}

              </div>

              <div className="shrink-0 border-t border-gray-100 bg-gray-50 p-4 flex gap-3">
                <button
                  type="button"
                  disabled={isStockPending}
                  onClick={() => { setStockModalType(null); setSelectedStockItem(null); }}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isStockPending}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium text-white shadow-sm transition-colors flex items-center justify-center ${stockModalType === 'in' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700'
                    }`}
                >
                  {isStockPending ? 'Saving...' : `Confirm Stock ${stockModalType === 'in' ? 'In' : 'Out'}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {printTarget && (
        <PrintBarcodeModal
          item={printTarget}
          variants={printVariants}
          onClose={() => setPrintTarget(null)}
        />
      )}

      {showImportChooser && !importConfig && (
        <ItemsImportChooser
          onClose={() => setShowImportChooser(false)}
          onSelect={(config) => {
            setImportConfig(config);
            setShowImportChooser(false);
          }}
        />
      )}

      {showBulkProductUpdateModal && (
        <BulkProductUpdateModal onClose={() => setShowBulkProductUpdateModal(false)} />
      )}

      {importConfig && (
        <EntityImportWizard
          config={importConfig}
          onClose={() => setImportConfig(null)}
          onSuccess={() => {
            qc.invalidateQueries({ queryKey: ['items'] });
            qc.invalidateQueries({ queryKey: ['categories'] });
          }}
        />
      )}

      {/* Bulk Print Labels Modal */}
      {showBulkPrintModal && (
        <BulkPrintLabelsModal onClose={() => setShowBulkPrintModal(false)} />
      )}

      {showBulkPriceModal && (
        <BulkPriceUpdateModal
          onClose={() => setShowBulkPriceModal(false)}
          onPrintLabels={() => setShowBulkPrintModal(true)}
        />
      )}

      {showCatalogExportModal && (
        <CatalogExportModal onClose={() => setShowCatalogExportModal(false)} />
      )}

      {showCustomFieldsModal && (
        <CustomFieldsModal onClose={() => setShowCustomFieldsModal(false)} />
      )}

      {/* Add Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 shrink-0">
              <h3 className="font-semibold text-gray-900">Add Category</h3>
              <button onClick={() => setShowCategoryModal(false)} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <form onSubmit={handleCategorySubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category Name</label>
                  <input required className="input-field" value={categoryFormData.category_name} onChange={(e) => setCategoryFormData({ ...categoryFormData, category_name: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea className="input-field" rows={3} value={categoryFormData.description} onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })} />
                  {!posSettings?.sell_only_products && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category Type</label>
                      <select className="input-field" value={categoryFormData.category_type} onChange={(e) => setCategoryFormData({ ...categoryFormData, category_type: e.target.value })}>
                        <option value="Product">Product</option>
                        <option value="Service">Service</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>
              <div className="shrink-0 border-t border-gray-100 bg-gray-50 p-4 flex gap-3">
                <button type="button" onClick={() => setShowCategoryModal(false)} className="btn-secondary flex-1">Cancel</button>
                <button type="submit" disabled={categoryMutation.isPending} className="btn-primary flex-1">
                  {categoryMutation.isPending ? 'Saving...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Brand Modal */}
      {showBrandModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 shrink-0">
              <h3 className="font-semibold text-gray-900">Add Brand</h3>
              <button onClick={() => setShowBrandModal(false)} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <form onSubmit={handleBrandSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Brand Name</label>
                  <input
                    required
                    className="input-field"
                    value={brandFormData.brand_name}
                    onChange={(e) => setBrandFormData({ ...brandFormData, brand_name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    className="input-field"
                    rows={3}
                    value={brandFormData.description}
                    onChange={(e) => setBrandFormData({ ...brandFormData, description: e.target.value })}
                  />
                </div>
                {brandError && <p className="text-sm text-red-600">{brandError}</p>}
              </div>
              <div className="shrink-0 border-t border-gray-100 bg-gray-50 p-4 flex gap-3">
                <button type="button" onClick={() => setShowBrandModal(false)} className="btn-secondary flex-1">Cancel</button>
                <button type="submit" disabled={brandMutation.isPending} className="btn-primary flex-1">
                  {brandMutation.isPending ? 'Saving...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Model Modal */}
      {showModelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 shrink-0">
              <h3 className="font-semibold text-gray-900">Add Model</h3>
              <button onClick={() => setShowModelModal(false)} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <form onSubmit={handleModelSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Model Name</label>
                  <input
                    required
                    className="input-field"
                    value={modelFormData.model_name}
                    onChange={(e) => setModelFormData({ ...modelFormData, model_name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    className="input-field"
                    rows={3}
                    value={modelFormData.description}
                    onChange={(e) => setModelFormData({ ...modelFormData, description: e.target.value })}
                  />
                </div>
                {modelError && <p className="text-sm text-red-600">{modelError}</p>}
              </div>
              <div className="shrink-0 border-t border-gray-100 bg-gray-50 p-4 flex gap-3">
                <button type="button" onClick={() => setShowModelModal(false)} className="btn-secondary flex-1">Cancel</button>
                <button type="submit" disabled={modelMutation.isPending} className="btn-primary flex-1">
                  {modelMutation.isPending ? 'Saving...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${deleteConfirm.errorMessage ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`}>
                <AlertCircle className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-950">
                {deleteConfirm.errorMessage ? t('confirmDelete.cannotDelete') : t('confirmDelete.title')}
              </h3>
            </div>
            
            <p className="text-sm text-gray-600 whitespace-pre-wrap">
              {deleteConfirm.errorMessage || t('confirmDelete.message', { label: deleteConfirm.label })}
            </p>

            <div className="flex justify-end gap-3 pt-2">
              {deleteConfirm.errorMessage ? (
                <button
                  type="button"
                  onClick={() => setDeleteConfirm(null)}
                  className="btn-primary w-24"
                >
                  {t('confirmDelete.ok')}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(null)}
                    className="btn-secondary"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (deleteConfirm.type === 'product') {
                        deleteMutation.mutate(deleteConfirm.id);
                      } else if (deleteConfirm.type === 'variant') {
                        variantDeleteMutation.mutate(deleteConfirm.id);
                      }
                    }}
                    disabled={deleteMutation.isPending || variantDeleteMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-red-300 rounded-lg transition-colors"
                  >
                    {deleteMutation.isPending || variantDeleteMutation.isPending ? t('confirmDelete.deleting') : t('common.delete')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}